import * as yup from 'yup';

import { useTranslations } from 'hooks/useTranslations';
import { EAddCaseType } from '.';
import { ECaseProcessTypes } from 'constants/CaseProcessTypes';
import { ECaseTaskNames } from 'constants/CaseTaskNames';

const useFormSchema = (caseType: EAddCaseType) => {
  const { t } = useTranslations();
  let schema = yup.object({});
  switch (caseType) {
    case EAddCaseType.APPLICATION:
      schema = yup.object({
        legalUniqueId: yup.string().required(t('general.field.validation.required')).trim(),
        licenceAuthorityUniqueId: yup
          .string()
          .required(t('general.field.validation.required'))
          .trim(),
        paymentStatusUniqueId: yup
          .string()
          .when('processTypeUniqueId', {
            is: ECaseProcessTypes.APPLICATION,
            then: (schema) => schema.required(t('general.field.validation.required')),
            otherwise: (schema) => schema.notRequired(),
          })
          .trim(),
        processTypeUniqueId: yup
          .string()
          .required(t('general.field.validation.required'))
          .trim(),
        licenceCodeName: yup
          .string()
          .when('processTypeUniqueId', {
            is: ECaseProcessTypes.APPLICATION,
            then: (schema) => schema.required(t('general.field.validation.required')),
            otherwise: (schema) => schema.notRequired(),
          })
          .trim(),
        taskNameUniqueId: yup
          .string()
          .when('processTypeUniqueId', {
            is: ECaseProcessTypes.OTHER_REQUEST,
            then: (schema) => schema.required(t('general.field.validation.required')),
            otherwise: (schema) => schema.notRequired(),
          })
          .trim(),
        weaponLicenceUniqueId: yup
          .string()
          .when('taskNameUniqueId', {
            is: ECaseTaskNames.REPRINT_LICENCE,
            then: (schema) => schema.required(t('general.field.validation.required')),
            otherwise: (schema) => schema.notRequired(),
          })
          .trim(),
        workerUniqueId: yup.string().trim(),
        description: yup
          .string()
          .when('processTypeUniqueId', {
            is: ECaseProcessTypes.OTHER_REQUEST,
            then: (schema) => schema.required(t('general.field.validation.required')),
            otherwise: (schema) => schema.notRequired(),
          })
          .max(1000, t('general.field.validation.maxCharacters').replace('{0}', '1000'))
          .trim(),
        applicationFiles: yup.array().when('processTypeUniqueId', {
          is: ECaseProcessTypes.APPLICATION,
          then: (schema) =>
            schema
              .required(t('general.field.validation.required'))
              .min(1, t('general.field.validation.required')),
          otherwise: (schema) => schema.notRequired(),
        }),

        documents: yup
          .object()
          .nullable()
          .test(
            'documents-required',
            t('general.field.validation.required'),
            function (documents) {
              const { documentWillUploadLetter } = this.parent;
              const { processTypeUniqueId } = this.parent;

              if (processTypeUniqueId === ECaseProcessTypes.OTHER_REQUEST) {
                return true;
              }

              if (!documents) return true;

              const errors: yup.ValidationError[] = [];

              for (const [id, files] of Object.entries(documents)) {
                const skip = documentWillUploadLetter?.[id];

                if (!skip) {
                  if (!files || (files as any[]).length === 0) {
                    errors.push(
                      this.createError({
                        path: `documents.${id}`,
                        message: t('general.field.validation.required'),
                      }),
                    );
                  }
                }
              }

              if (errors.length > 0) {
                return new yup.ValidationError(errors);
              }

              return true;
            },
          ),
      });
      break;
    case EAddCaseType.VARY:
      schema = yup.object({
        legalUniqueId: yup.string().required(t('general.field.validation.required')).trim(),
        licenceAuthorityUniqueId: yup
          .string()
          .required(t('general.field.validation.required'))
          .trim(),
        paymentStatusUniqueId: yup
          .string()
          .required(t('general.field.validation.required'))
          .trim(),
        processTypeUniqueId: yup
          .string()
          .required(t('general.field.validation.required'))
          .trim(),
        description: yup
          .string()
          .required(t('general.field.validation.required'))
          .max(1000, t('general.field.validation.maxCharacters').replace('{0}', '1000'))
          .trim(),
        applicationProcessUniqueId: yup
          .string()
          .required(t('general.field.minOneOptionRequired.required'))
          .trim(),
        documents: yup
          .object()
          .nullable()
          .test(
            'documents-required',
            t('general.field.validation.required'),
            function (documents) {
              const { documentWillUploadLetter } = this.parent;

              if (!documents) return true;

              const errors: yup.ValidationError[] = [];

              for (const [id, files] of Object.entries(documents)) {
                const skip = documentWillUploadLetter?.[id];

                if (!skip) {
                  if (!files || (files as any[]).length === 0) {
                    errors.push(
                      this.createError({
                        path: `documents.${id}`,
                        message: t('general.field.validation.required'),
                      }),
                    );
                  }
                }
              }

              if (errors.length > 0) {
                return new yup.ValidationError(errors);
              }

              return true;
            },
          ),
      });
      break;
    case EAddCaseType.RENEW:
      schema = yup.object({
        legalUniqueId: yup.string().required(t('general.field.validation.required')).trim(),
        licenceAuthorityUniqueId: yup
          .string()
          .required(t('general.field.validation.required'))
          .trim(),
        paymentStatusUniqueId: yup
          .string()
          .required(t('general.field.validation.required'))
          .trim(),
        applicationFiles: yup
          .array()
          .min(1, t('general.field.validation.required'))
          .required(t('general.field.validation.required')),
        documents: yup
          .object()
          .nullable()
          .test(
            'documents-required',
            t('general.field.validation.required'),
            function (documents) {
              const { documentWillUploadLetter } = this.parent;

              if (!documents) return true;

              const errors: yup.ValidationError[] = [];

              for (const [id, files] of Object.entries(documents)) {
                const skip = documentWillUploadLetter?.[id];

                if (!skip) {
                  if (!files || (files as any[]).length === 0) {
                    errors.push(
                      this.createError({
                        path: `documents.${id}`,
                        message: t('general.field.validation.required'),
                      }),
                    );
                  }
                }
              }

              if (errors.length > 0) {
                return new yup.ValidationError(errors);
              }

              return true;
            },
          ),
      });
      break;
    default:
      break;
  }

  return { schema };
};

export default useFormSchema;
